import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { translateCopy, translations } from './copyTranslations'

const STORAGE_KEY = 'solarlight-language'
const DEFAULT_LANGUAGE = 'fr'

const LanguageContext = createContext(null)
const TRANSLATED_ATTRIBUTES = ['aria-label', 'placeholder', 'title', 'alt']
const SKIPPED_TEXT_PARENTS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA'])

const getInitialLanguage = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE
  }

  const storedLanguage = window.localStorage.getItem(STORAGE_KEY)
  return translations[storedLanguage] ? storedLanguage : DEFAULT_LANGUAGE
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage)
  const translateContent = useCallback((value) => translateCopy(value, language), [language])

  const translateElementTree = useCallback((root) => {
    if (!root || typeof document === 'undefined') {
      return
    }

    const textWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement

        if (!parent || SKIPPED_TEXT_PARENTS.has(parent.tagName) || parent.closest('[data-no-translate]')) {
          return NodeFilter.FILTER_REJECT
        }

        return node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
      }
    })

    const textNodes = []
    while (textWalker.nextNode()) {
      textNodes.push(textWalker.currentNode)
    }

    textNodes.forEach((node) => {
      const translated = translateCopy(node.nodeValue, language)
      if (translated !== node.nodeValue) {
        node.nodeValue = translated
      }
    })

    const elements = root.querySelectorAll ? [root, ...root.querySelectorAll('*')] : []
    elements.forEach((element) => {
      if (!element.getAttribute || element.closest('[data-no-translate]')) {
        return
      }

      TRANSLATED_ATTRIBUTES.forEach((attribute) => {
        const currentValue = element.getAttribute(attribute)
        if (!currentValue) {
          return
        }

        const translated = translateCopy(currentValue, language)
        if (translated !== currentValue) {
          element.setAttribute(attribute, translated)
        }
      })
    })
  }, [language])

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language)
    document.documentElement.lang = language
  }, [language])

  useEffect(() => {
    if (typeof document === 'undefined' || !document.body) {
      return undefined
    }

    let frameId = null
    const scheduleTranslate = () => {
      if (frameId) {
        return
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null
        translateElementTree(document.body)
      })
    }

    scheduleTranslate()

    const observer = new MutationObserver((mutations) => {
      const hasVisibleChanges = mutations.some((mutation) => (
        mutation.type === 'childList' ||
        (mutation.type === 'attributes' && TRANSLATED_ATTRIBUTES.includes(mutation.attributeName))
      ))

      if (hasVisibleChanges) {
        scheduleTranslate()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: TRANSLATED_ATTRIBUTES
    })

    return () => {
      observer.disconnect()
      if (frameId) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [translateElementTree])

  const value = useMemo(() => {
    const t = (key) => translations[language]?.[key] || translations[DEFAULT_LANGUAGE][key] || key
    const toggleLanguage = () => {
      setLanguage((current) => (current === 'fr' ? 'en' : 'fr'))
    }

    return { language, setLanguage, t, toggleLanguage, translateContent }
  }, [language, translateContent])

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }

  return context
}
